import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatCurrency } from '../CoreFunctions';

/**
 * Sleek summary card for Residential Property top metrics.
 * Props:
 *  - property: the ResidentialProperty object (includes id and metadata)
 *  - onChangePhoto?: (event) => void  // handler from parent to upload/replace photo
 *  - uploading?: boolean              // uploading state for photo
 *  - error?: string                   // error text for photo upload
 */
const PropertySummaryCard = ({ property, onChangePhoto, uploading = false, error = '' }) => {
  const assetId = property?.id;
  const [loading, setLoading] = useState(false);
  const [latestMarket, setLatestMarket] = useState(null);
  const [latestBook, setLatestBook] = useState(null);
  const [helocs, setHelocs] = useState([]);
  const [latestTaxAssessment, setLatestTaxAssessment] = useState(null);
  const [editingDeed, setEditingDeed] = useState(false);
  const [deedUrl, setDeedUrl] = useState(property?.deed_link || '');
  const [savingDeed, setSavingDeed] = useState(false);

  const [tcoData, setTcoData] = useState({
    ytd: { total: 0, operating: 0, financing: 0, capex: 0 },
    priorYear: { total: 0, operating: 0, financing: 0, capex: 0 },
    total: { total: 0, operating: 0, financing: 0, capex: 0 }
  });

  const loadTCO = async () => {
    if (!assetId) return;
    try {
      const now = new Date();
      const currentYear = now.getFullYear();
      const jan1Current = `${currentYear}-01-01`;
      const dec31Prior = `${currentYear - 1}-12-31`;
      const jan1Prior = `${currentYear - 1}-01-01`;

      const fetchSum = async (start, end) => {
        const params = {
          only_txn_backed: 1,
          include_children: 1
        };
        if (start) params.start_date = start;
        if (end) params.end_date = end;

        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/${assetId}/linked-expenses/`, { params });
        const actuals = res.data?.actual || [];
        
        // TCO includes Operating expenses, Financing (Interest/Fees), and CapEx (Enhancements, Principal)
        // We exclude 'initial_purchase' from CapEx to avoid massive price inflation in Total TCO
        const opFiltered = actuals.filter(item => item.expense_group === 'Operating');
        const finFiltered = actuals.filter(item => item.expense_group === 'Financing');
        const capexFiltered = actuals.filter(item => item.expense_group === 'CapEx' && item.expense_category !== 'initial_purchase');
        
        const opSum = opFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        const finSum = finFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        const capexSum = capexFiltered.reduce((acc, item) => acc + (parseFloat(item.amount) || 0), 0);
        
        return { total: opSum + finSum + capexSum, operating: opSum, financing: finSum, capex: capexSum };
      };

      const [ytd, prior, total] = await Promise.all([
        fetchSum(jan1Current, null),
        fetchSum(jan1Prior, dec31Prior),
        fetchSum(null, null)
      ]);

      setTcoData({ ytd, priorYear: prior, total });
    } catch (e) {
      console.error('Failed to load TCO metrics for property', e);
    }
  };

  useEffect(() => {
    loadTCO();
  }, [assetId]);

  useEffect(() => {
    let ignore = false;
    const fetchLatestVals = async () => {
      if (!assetId) return;
      setLoading(true);
      try {
        const res = await axios.get(`/api/balance_sheet/assets/valuations/${assetId}/`);
        const pickLatest = (arr) => {
          if (!Array.isArray(arr) || arr.length === 0) return null;
          // ensure sorted by date asc
          const sorted = [...arr]
            .filter(r => r && r.date && (r.value !== null && r.value !== undefined))
            .sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));
          return sorted.length ? sorted[sorted.length - 1] : null;
        };
        const market = pickLatest(res?.data?.market);
        const book = pickLatest(res?.data?.book);
        if (!ignore) {
          setLatestMarket(market);
          setLatestBook(book);
        }
      } catch (e) {
        if (!ignore) {
          setLatestMarket(null);
          setLatestBook(null);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchLatestVals();
    return () => { ignore = true; };
  }, [assetId]);

  // Fetch associated HELOCs for this residential property (if any)
  useEffect(() => {
    let ignore = false;
    const fetchHelocs = async () => {
      if (!assetId) return;
      try {
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${assetId}/helocs/`);
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!ignore) setHelocs(rows);
      } catch (e) {
        if (!ignore) setHelocs([]);
      }
    };
    fetchHelocs();
    return () => { ignore = true; };
  }, [assetId]);

  useEffect(() => {
    let ignore = false;
    const fetchLatestTaxAssessment = async () => {
      if (!assetId) return;
      try {
        const res = await axios.get(`/api/balance_sheet/assets/physical_assets/resi_properties/${assetId}/tax-assessments/`);
        const assessments = Array.isArray(res?.data) ? res.data : [];
        if (assessments.length > 0) {
          // Sort by year desc and get the first one
          const latest = assessments.sort((a, b) => b.assessment_year - a.assessment_year)[0];
          if (!ignore) setLatestTaxAssessment(latest);
        }
      } catch (e) {
        console.error('Failed to load latest tax assessment', e);
      }
    };
    fetchLatestTaxAssessment();
    return () => { ignore = true; };
  }, [assetId]);

  useEffect(() => {
    setDeedUrl(property?.deed_link || '');
  }, [property?.deed_link]);

  const handleDeedSave = async () => {
    if (!assetId) return;
    setSavingDeed(true);
    try {
      await axios.patch(`/api/balance_sheet/assets/physical_assets/resi_properties/${assetId}/`, {
        deed_link: deedUrl
      });
      setEditingDeed(false);
      // We might want to trigger a reload of the property in the parent, 
      // but for now, the local state 'deedUrl' is updated.
    } catch (e) {
      console.error('Failed to save deed link', e);
      alert('Failed to save deed link');
    } finally {
      setSavingDeed(false);
    }
  };

  // Helpers for safe formatting
  // Currency with no decimals (rounded to nearest dollar)
  const fmtMoney0 = (val) => {
    const num = Number(val);
    if (!isFinite(num)) return '—';
    const rounded = Math.round(num);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(rounded);
    } catch (e) {
      // Fallback to existing formatter if Intl fails, then strip decimals
      const s = formatCurrency(rounded);
      return typeof s === 'string' ? s.replace(/\.\d{2}$/,'') : s;
    }
  };
  const getMoney = (moneyLike) => {
    if (moneyLike == null) return null;
    try {
      if (typeof moneyLike === 'object') {
        if (moneyLike.amount != null) return Number(moneyLike.amount);
        // some serializers may nest value in another level
        if (moneyLike.value != null) return Number(moneyLike.value);
      }
      return Number(moneyLike);
    } catch {
      return null;
    }
  };
  const fmtRatio = (val) => {
    const num = Number(val);
    if (!isFinite(num) || num <= 0) return '—';
    return `${num.toFixed(2)}x`;
  };
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso + (iso.length <= 10 ? 'T00:00:00' : ''));
      return d.toLocaleDateString();
    } catch {
      return iso;
    }
  };

  // Derive basic fields
  const address = (() => {
    const a = property?.address || {};
    const parts = [a.street_address, a.city, a.state, a.zip_code].filter(Boolean);
    if (parts.length) return parts.join(', ');
    return property?.description || 'Property';
  })();

  const purchasePrice = (() => {
    const ip = property?.initial_purchase_price;
    if (!ip) return null;
    const money = ip.amount;
    // money may be object or number; try common shapes
    if (money && typeof money === 'object' && money.amount != null) return Number(money.amount);
    return Number(money);
  })();
  const purchaseDate = property?.purchase_date || property?.initial_purchase_price?.date || null;

  const outstandingDebt = property?.current_outstanding_debt;
  const bvEquity = property?.current_bv_equity;
  const deRatio = property?.current_bv_debt_to_equity;

  // Financing snapshot (at close)
  const financing = property?.financing || {};
  const financingAmount = getMoney(financing.amount);
  const financingEquity = getMoney(financing.total_equity_amount);
  const financingDebt = getMoney(financing.total_debt_amount);
  const moveInDate = property?.move_in_date || null;
  // If there is exactly one linked debt, return its id for linking purposes
  const singleDebtId = (() => {
    const df = financing?.debt_financing;
    if (!df) return null;
    const ids = new Set();
    if (Array.isArray(df)) {
      for (const d of df) {
        if (!d) continue;
        const id = d.id ?? d.pk ?? null;
        if (id != null) ids.add(id);
      }
    } else if (typeof df === 'object') {
      for (const [k, d] of Object.entries(df)) {
        const id = d?.id ?? k ?? null;
        if (id != null) ids.add(id);
      }
    }
    return ids.size === 1 ? Array.from(ids)[0] : null;
  })();

  const linkIfSingleDebt = (valueNode) => {
    if (!singleDebtId) return valueNode;
    return (
      <a href={`/balance_sheet/financing/mortgages/${singleDebtId}`} target="_blank" rel="noopener noreferrer">{valueNode}</a>
    );
  };

  const items = [
    { key: 'address', label: 'Address', value: address },
    { key: 'purchase_price', label: 'Purchase Price', value: fmtMoney0(purchasePrice) },
    { key: 'purchase_date', label: 'Purchase Date', value: fmtDate(purchaseDate) },
    { key: 'move_in_date', label: 'Move-in Date', value: fmtDate(moveInDate) },
    { key: 'fin_equity', label: 'Initial Equity', value: fmtMoney0(financingEquity) },
    { key: 'fin_debt', label: 'Initial Debt', value: financingDebt != null ? linkIfSingleDebt(fmtMoney0(financingDebt)) : '—' },
    { key: 'market_value', label: 'Latest Market Value', value: fmtMoney0(latestMarket?.value), hint: fmtDate(latestMarket?.date) },
    { key: 'book_value', label: 'Latest Book Value', value: fmtMoney0(latestBook?.value), hint: fmtDate(latestBook?.date) },
    // Total outstanding debt = amortizing mortgage(s) + HELOC balances
    { key: 'outstanding_debt', label: 'Outstanding Debt (Total)', value: (() => {
      const helocTotal = (helocs || []).reduce((sum, h) => sum + (Number(h?.current_balance) || 0), 0);
      const baseDebt = Number(outstandingDebt || 0);
      const total = baseDebt + helocTotal;
      // Only link when there is exactly one non-HELOC amortizing debt
      const node = fmtMoney0(total);
      return (singleDebtId && helocTotal === 0) ? linkIfSingleDebt(node) : node;
    })() },
    // Sub-components of outstanding debt
    { key: 'outstanding_debt_mort', label: 'Outstanding Debt — Mortgage', value: (() => {
      const baseDebt = Number(outstandingDebt || 0);
      const node = fmtMoney0(baseDebt);
      return singleDebtId ? linkIfSingleDebt(node) : node;
    })() },
    { key: 'outstanding_debt_heloc', label: 'Outstanding Debt — HELOCs', value: (() => {
      const helocTotal = (helocs || []).reduce((sum, h) => sum + (Number(h?.current_balance) || 0), 0);
      const node = fmtMoney0(helocTotal);
      // If exactly one HELOC is attached, link to its detail page in a new tab
      const helocIds = Array.isArray(helocs) ? helocs.map(h => h?.id).filter(id => id != null) : [];
      const uniqueIds = Array.from(new Set(helocIds));
      if (uniqueIds.length === 1) {
        const hid = uniqueIds[0];
        return (
          <a href={`/balance_sheet/financing/revolvers/helocs/${hid}`} target="_blank" rel="noopener noreferrer">{node}</a>
        );
      }
      return node;
    })() },
    { key: 'bv_equity', label: 'BV Equity', value: fmtMoney0(bvEquity) },
    { key: 'de_ratio', label: 'Debt / Equity', value: fmtRatio(deRatio) },
    // Placeholders carried over from legacy text block
    { key: 'tco_ytd', label: 'TCO (YTD / Prior / Total)', value: (
      <span style={{ color: '#fbbf24' }}>
        <span title={`Op: ${formatCurrency(tcoData.ytd.operating)}, Fin: ${formatCurrency(tcoData.ytd.financing)}, CapEx: ${formatCurrency(tcoData.ytd.capex)}`}>
          ${Math.round(tcoData.ytd.total).toLocaleString()}
        </span>
        {' / '}
        <span title={`Op: ${formatCurrency(tcoData.priorYear.operating)}, Fin: ${formatCurrency(tcoData.priorYear.financing)}, CapEx: ${formatCurrency(tcoData.priorYear.capex)}`}>
          ${Math.round(tcoData.priorYear.total).toLocaleString()}
        </span>
        {' / '}
        <span style={{ fontWeight: 'bold' }} title={`Op: ${formatCurrency(tcoData.total.operating)}, Fin: ${formatCurrency(tcoData.total.financing)}, CapEx: ${formatCurrency(tcoData.total.capex)}`}>
          ${Math.round(tcoData.total.total).toLocaleString()}
        </span>
      </span>
    ) },
    { key: 'deed', label: 'Deed', value: (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {editingDeed ? (
          <div style={{ display: 'flex', gap: '4px' }}>
            <input 
              type="text" 
              value={deedUrl} 
              onChange={(e) => setDeedUrl(e.target.value)}
              placeholder="URL to deed"
              style={{ fontSize: '11px', background: '#1f2937', color: '#fff', border: '1px solid #374151', borderRadius: '4px', padding: '2px 4px', width: '120px' }}
            />
            <button 
              onClick={handleDeedSave}
              disabled={savingDeed}
              style={{ padding: '2px 4px', fontSize: '10px', borderRadius: '4px', cursor: 'pointer', background: '#3b82f6', color: '#fff', border: 'none' }}
            >
              {savingDeed ? '...' : 'Save'}
            </button>
            <button 
              onClick={() => { setEditingDeed(false); setDeedUrl(property?.deed_link || ''); }}
              style={{ padding: '2px 4px', fontSize: '10px', borderRadius: '4px', cursor: 'pointer', background: '#4b5563', color: '#fff', border: 'none' }}
            >
              ×
            </button>
          </div>
        ) : (
          <>
            {deedUrl ? (
              <a href={deedUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'underline', color: '#60a5fa', marginRight: '4px' }}>View Deed</a>
            ) : (
              <span style={{ color: '#9ca3af', marginRight: '4px' }}>—</span>
            )}
            <button 
              onClick={() => setEditingDeed(true)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '12px', padding: '0 4px' }}
              title="Edit deed link"
            >
              ✎
            </button>
          </>
        )}
      </div>
    ) },
    { key: 'tax_assessments', label: 'Tax Assessments', value: latestTaxAssessment ? (
      <span style={{ fontSize: '12px' }}>
        Assessed: {fmtMoney0(latestTaxAssessment.total_assessed_value)} / Taxable: {fmtMoney0(latestTaxAssessment.total_taxable_value)}
        <span style={{ color: '#9ca3af', marginLeft: '4px' }}>({latestTaxAssessment.assessment_year})</span>
      </span>
    ) : '—' },
  ];

  // Prefer thumbnail when present
  const photoThumb = property?.photo_thumb || null;
  const hasPhoto = !!(property?.photo || property?.photo_thumb);

  return (
    <section className="summary-card" aria-label="Property summary">
      <div className="summary-header" style={{ display: 'flex', alignItems: 'center' }}>
        <div className="summary-title">Property Summary</div>
        {loading && <div className="summary-loading" style={{ marginLeft: 'auto' }}>Updating…</div>}
      </div>

      {/* Two-column layout: metrics on the left, photo panel on the right */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 16, alignItems: 'start' }}>
        <div className="metrics-grid">
          {items.map((it) => (
            <div key={it.key} className="metric" title={typeof it.value === 'string' ? it.value : undefined}>
              <div className="metric-label">{it.label}</div>
              <div className="metric-value" style={{ overflow: 'visible' }}>{it.value ?? '—'}</div>
              {it.hint && <div className="metric-hint">as of {it.hint}</div>}
            </div>
          ))}
        </div>

        {/* Photo panel (right side within Property Summary card) */}
        <div style={{ position: 'relative' }}>
          <div style={{ border: '1px solid #374151', background: '#0b1220', borderRadius: 8, overflow: 'hidden' }}>
            {photoThumb ? (
              <img src={photoThumb} alt="Property" style={{ width: '100%', height: 200, objectFit: 'cover', display: 'block' }} loading="lazy" />
            ) : (
              <div style={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: 14 }}>
                No photo yet
              </div>
            )}
          </div>
          {typeof onChangePhoto === 'function' && (
            <label style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.6)', color: '#fff', borderRadius: 20, padding: '6px 10px', cursor: 'pointer', fontSize: 12 }}>
              {uploading ? 'Uploading…' : (hasPhoto ? 'Change photo' : 'Upload photo')}
              <input type="file" accept="image/*" onChange={onChangePhoto} style={{ display: 'none' }} />
            </label>
          )}
          {!!error && <div style={{ color: '#f87171', fontSize: 12, marginTop: 6 }}>{error}</div>}
        </div>
      </div>
    </section>
  );
};

export default PropertySummaryCard;
